/**
 * Service para chamadas ao endpoint /api/welcome
 * POST para criar welcome, GET para recuperar resumo
 * Também constrói o payload a partir do estado do hook
 */

import { authService } from './authService';
import { getWelcomeText, getWelcomeList, interpolateList } from '../utils/welcomeText';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Obtém headers com token de autenticação
 */
const getAuthHeaders = () => {
  const token = authService.getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

/**
 * POST /api/welcome — cria todo o setup do welcome
 * 
 * @param {Object} payload - WelcomeRequestDto
 * @returns {Promise<Object>} { status: 'created'|'conflict', data: WelcomeResponseDto }
 */
export const postWelcome = async (payload) => {
  try {
    const url = `${API_BASE_URL}/welcome`;
    console.log('[welcomeService.postWelcome] Enviando payload:', payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    // Sucesso
    if (response.status === 200) {
      const data = await response.json();
      return { status: 'created', data };
    }

    // Conflito — usuário já fez welcome
    if (response.status === 409) {
      return { status: 'conflict' };
    }

    // Erro — tenta extrair mensagem
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `API Error: ${response.status} ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('[welcomeService.postWelcome] Erro:', error);
    throw error;
  }
};

/**
 * GET /api/welcome — recupera resumo do welcome já criado
 * Usado em case de 409 (duplo clique) ou timeout na Tela 9
 * 
 * @returns {Promise<Object>} WelcomeResponseDto
 */
export const getWelcomeSummary = async () => {
  try {
    const url = `${API_BASE_URL}/welcome`;
    console.log('[welcomeService.getWelcomeSummary] Recuperando resumo...');

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    // Sucesso
    if (response.status === 200) {
      const data = await response.json();
      return data;
    }

    // Não encontrado
    if (response.status === 404) {
      return null;
    }

    // Erro
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `API Error: ${response.status} ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('[welcomeService.getWelcomeSummary] Erro:', error);
    throw error;
  }
};

/**
 * Confere o payload contra as MESMAS regras que o `WelcomeRequestValidator` da API aplica, ANTES
 * de postar — e diz qual tela reabrir quando algo falta.
 *
 * POR QUE EXISTE (varredura de erros 2026-09-10): o wizard conseguia montar um payload que a API
 * recusa SEMPRE, e o `submit()` só guardava a mensagem em `state.error` — o usuário ficava na tela
 * de processamento, sem nenhuma tela onde corrigir. Caso real: uma conta tentou concluir o Welcome
 * 27x em 2 minutos ("O local com ID '0' está sem nome"), tomou 400 nas 27 e foi abandonada vazia.
 * Antes disso, em agosto, o mesmo padrão com outro campo (`customBusinessType`). O que não morria
 * era a FORMA do bug: 400 garantido, sem volta.
 *
 * Por isso o guard é GENÉRICO (varre as regras e devolve o passo dono do campo), não um `if` por
 * campo — senão o próximo campo obrigatório repete a história.
 *
 * Espelho de `Blasoft.Agendario.Application/Validators/WelcomeRequestValidator.cs`. Se uma regra
 * mudar lá, muda aqui. As duas listas devem casar; divergir só troca um 400 sem saída por um
 * bloqueio sem saída, que é igualmente ruim.
 *
 * @param {Object} payload - saída de buildWelcomePayload
 * @param {string[]} steps - array de steps do wizard (para resolver o índice de destino)
 * @returns {{ok: boolean, stepKey?: string, stepIndex?: number, message?: string}}
 */
export const validateWelcomePayload = (payload, steps = []) => {
  const entities = payload?.entities ?? {};
  const places = entities.places ?? {};
  const professionals = entities.professionals ?? {};
  const services = entities.services ?? {};

  const vazio = (v) => typeof v !== 'string' || v.trim().length === 0;

  // A ordem importa: reabrimos a PRIMEIRA tela pendente, seguindo a ordem do wizard.
  const regras = [
    // Regra 1 do validator — themeId obrigatório.
    {
      falha: () => vazio(payload?.themeId),
      stepKey: 'theme',
      message: 'Escolha o ramo do seu negócio para continuar.',
    },
    // Regras 3 e 5b — pelo menos um local, e com nome.
    {
      falha: () => Object.keys(places).length === 0 || Object.values(places).some((p) => vazio(p?.name)),
      stepKey: 'locationName',
      message: 'Dê um nome ao seu local de atendimento para continuar.',
    },
    // Regras 4 e 5b — pelo menos um profissional, com a chave "0" (o dono), e com nome.
    {
      falha: () =>
        Object.keys(professionals).length === 0 ||
        !Object.prototype.hasOwnProperty.call(professionals, '0') ||
        Object.values(professionals).some((p) => vazio(p?.name)),
      stepKey: 'team',
      message: 'Informe o nome de quem vai atender para continuar.',
    },
    // Regras 5 e 5b — pelo menos um serviço, e com nome.
    {
      falha: () => Object.keys(services).length === 0 || Object.values(services).some((s) => vazio(s?.name)),
      stepKey: 'services',
      message: 'Cadastre pelo menos um serviço (com nome) para continuar.',
    },
  ];

  for (const regra of regras) {
    if (!regra.falha()) continue;

    const stepIndex = steps.indexOf(regra.stepKey);
    return {
      ok: false,
      stepKey: regra.stepKey,
      // -1 quando o passo não está na lista (ex.: 'theme' omitido). Quem chama decide o fallback:
      // é melhor mostrar o erro parado do que pular para um índice inválido.
      stepIndex,
      message: regra.message,
    };
  }

  return { ok: true };
};

/**
 * Constrói o payload WelcomeRequestDto a partir do estado do hook
 * Converte o estado do wizard no formato que a API espera
 *
 * @param {Object} state - Estado do useWelcomeWizard
 * @param {Object} template - Template carregado do tema
 * @returns {Object} WelcomeRequestDto pronto para POST
 *
 * IMPORTANTE: esta função é SÍNCRONA. Já foi async no passado e o submit()
 * chamava sem await — o resultado era uma Promise serializada como '{}'.
 */
export const buildWelcomePayload = (state, template) => {
  try {
    // ========================
    // 1. Dicionários de entidades
    // ========================
    const entities = {
      professionals: {},
      services: {},
      places: { '0': { name: state.locationName } },
    };

    // Professionals: ID 0 é o usuário — cada valor é { name } (WelcomeEntityProfessionalDto)
    state.professionals.forEach((prof, idx) => {
      entities.professionals[String(idx)] = { name: prof.name };
    });

    // Services — cada valor é { name } (WelcomeEntityServiceDto)
    state.services.forEach((svc, idx) => {
      entities.services[String(idx)] = { name: svc.name };
    });

    // ========================
    // 2. Detalhes de serviços (preço + duração + maxGroupSize)
    // ========================
    const serviceDetails = {};
    state.services.forEach((svc, idx) => {
      const detail = state.serviceDetails[svc.id] || {};
      const groupSize = state.groupSizes[svc.id] ?? 1;

      serviceDetails[String(idx)] = {
        price: detail.price ?? null,
        durationMinutes: detail.durationMinutes ?? 60,
        maxGroupSize: groupSize,
      };
    });

    // ========================
    // 3. Estrutura de vínculos
    // ========================
    const structure = {
      places: [
        {
          entityId: 0,
          professionals: state.professionals.map((prof, profIdx) => ({
            entityId: profIdx,
            serviceIds: prof.serviceIds ?? state.services.map((_, idx) => idx),
            schedule: prof.schedule || { shifts: [] },
          })),
        },
      ],
    };

    // ========================
    // 4. Conteúdo temático (copy interpolada)
    // ========================
    const firstName = state.userName.split(' ')[0].trim();
    const businessNoun =
      template?.welcome?.businessNoun ||
      state.customBusinessType ||
      'negócio';

    const interpolationVars = {
      firstName,
      businessNoun,
      locationName: state.locationName,
      professionalName: state.userName,
    };

    // Obtém textos temáticos (getWelcomeText / getWelcomeList são síncronos)
    const motivationalNote = getWelcomeText(
      template,
      'motivationalAppointmentNote',
      interpolationVars
    );

    const emailSubject = getWelcomeText(template, 'emailSubject', interpolationVars);
    const emailGreeting = getWelcomeText(template, 'emailGreeting', interpolationVars);
    const emailIntro = getWelcomeText(template, 'emailIntro', interpolationVars);

    // Próximos passos
    let summarySteps = getWelcomeList(template, 'summarySteps');
    summarySteps = interpolateList(summarySteps, interpolationVars);

    const content = {
      motivationalAppointmentNote: motivationalNote,
      email: {
        subject: emailSubject,
        greeting: emailGreeting,
        intro: emailIntro,
        nextSteps: summarySteps.map((step) =>
          typeof step === 'object' ? step.text : step
        ),
      },
    };

    // ========================
    // 5. Payload final
    // ========================
    const payload = {
      themeId: state.themeId,
      customBusinessType: state.customBusinessType,
      entities,
      serviceDetails,
      structure,
      content,
    };

    console.log('[welcomeService.buildWelcomePayload] Payload construído:', payload);
    return payload;
  } catch (error) {
    console.error('[welcomeService.buildWelcomePayload] Erro ao construir payload:', error);
    throw new Error(`Falha ao construir payload: ${error.message}`);
  }
};
